import React from 'react';
import withFacebookParser from '../FacebookPluginParser';
import { extendWithDefaults, LifeCycleThis } from '@mm/utils';
import {
  compose,
  lifecycle,
  onlyUpdateForKeys,
  withHandlers,
  mapProps,
} from 'recompose';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import { StoreState } from '@mm/state/store-interfaces';
import { connect } from 'react-redux';
import { FacebookStaticSDK } from '@mm/state/interfaces/facebook';

type OuterProps = {
  appId: string;
  pageId: string;
  passthroughParams?: string;
  color?: string;
  size?: string;
  ctaText?: string;
  onRendered?: () => any;
  onClick?: () => any;
  onNotYou?: () => any;
  onOptIn?: () => any;

  // Only used to force re-render
  triggerRender?: boolean;
};

type InnerProps = ReturnType<typeof mapStateToProps> & {
  onMessengerEvent: (event: any) => void;
};

type Props = OuterProps & InnerProps;

type MessengerEvent = {
  event: 'rendered' | 'clicked' | 'not_you' | 'opt_in';
  is_after_optin: boolean;
  ref: string;
};

const mapStateToProps = (state: StoreState) => ({
  isInitialized: state.facebook.isInitialized,
  session: state.session.session,
});

const onMessengerEvent = (props: Props) => (e: MessengerEvent) => {
  const { onRendered, onClick, onNotYou, onOptIn, passthroughParams } = props;
  const { event, ref } = e;

  // If not the same ref don't call callbacks
  if (ref === passthroughParams) {
    const eventCallbackMap = {
      rendered: onRendered,
      clicked: onClick,
      not_you: onNotYou,
      opt_in: onOptIn,
    };

    const eventCallback = eventCallbackMap[event];

    if (eventCallback) {
      eventCallback();
    }
  }
};

async function componentDidMount(this: LifeCycleThis<Props>) {
  const {
    onRendered,
    onClick,
    onNotYou,
    onOptIn,
    passthroughParams,
    onMessengerEvent,
  } = this.props;
  let facebookSDK: FacebookStaticSDK | null = null;
  try {
    facebookSDK = await facebookSDKActions.getFacebookSDK();
  } catch {
    return;
  }
  const someCallbackSet = onRendered || onClick || onNotYou || onOptIn;

  if (someCallbackSet && facebookSDK) {
    if (passthroughParams) {
      // @ts-ignore
      facebookSDK.Event.subscribe('send_to_messenger', onMessengerEvent);
    } else {
      console.warn(
        'Can only use event callback if pass ref through passthroughParams',
      );
    }
  }
}

async function componentWillUnmount(this: LifeCycleThis<Props>) {
  const { onMessengerEvent, isInitialized } = this.props;

  if (isInitialized) {
    let facebookSDK: FacebookStaticSDK | null = null;
    try {
      facebookSDK = await facebookSDKActions.getFacebookSDK();
    } catch {
      return;
    }
    if (facebookSDK) {
      // @ts-ignore
      facebookSDK.Event.unsubscribe('send_to_messenger', onMessengerEvent);
    }
  }
}

// Helps to stop re-renders when arrow functions are passed as event callbacks
const withStopUnneededRenders = onlyUpdateForKeys([
  'appId',
  'pageId',
  'passthroughParams',
  'color',
  'size',
  'ctaText',
  'triggerRender',
]);

const remapProps = (
  ownerProps: OuterProps & ReturnType<typeof mapStateToProps>,
) => ({
  ...ownerProps,
  passthroughParams:
    ownerProps.session && ownerProps.session.searchToken
      ? `${ownerProps.passthroughParams}_${ownerProps.session.searchToken}`
      : ownerProps.passthroughParams,
});

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps),
  mapProps(remapProps),
  withHandlers({ onMessengerEvent }),
  lifecycle<Props, {}>({ componentDidMount, componentWillUnmount }),
  withStopUnneededRenders,
  // withFacebookParser needs to be last
  withFacebookParser,
);

const defaultProps = {
  color: 'blue',
  size: 'standard',
};

const SendToMessenger = enhance((props: Props) => {
  const { appId, pageId, passthroughParams, color, size, ctaText, session } =
    extendWithDefaults({ props, defaultProps });

  return React.createElement('div', {
    className: 'fb-send-to-messenger',
    messenger_app_id: appId,
    page_id: pageId,
    'data-color': color,
    'data-size': size,
    'data-ref': passthroughParams,
    cta_text: ctaText,
  });
});

export default SendToMessenger;
